import React, { useEffect, useState } from 'react';
import { Button, Grid, Alert, Typography, IconButton, FormControl, InputLabel, Select, MenuItem, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PlusIcon from '@mui/icons-material/Add';
import SimpleDialog from 'components/dialogs/Dialog';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormInputNumber } from 'components/form/FormInputNumber';
import { FormInputPercentage, InputPercentageTextField } from 'components/form/FormInputPercentage';
import SimpleTable from 'components/table/SimpleTable';
import { DownpaymentFormType, downpaymentSchema, OfferVersionFormType } from 'components/template_editor/context';
import i18next from 'i18next';
import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { useDispatch } from 'react-redux';
import { isDownpaymentValid } from 'semshared/offer/downpayment';
import Price from 'components/Price';
import { FormInputDate } from 'components/form/FormInputDate';
import { FormInputText, FormInputTextArea } from 'components/form/FormInputText';
import { FormInputCurrency } from 'components/form/FormInputCurrency';

interface DownpaymentDialogProps {
  open: boolean;
  onCancel: () => void;
  onSave: (downpayment: DownpaymentFormType) => Promise<void>;
  initialDownpayment?: DownpaymentFormType;
  guests: number;
  rooms: number;
  totalPrice: number;
  currency: string;
}

type CalculationType = 'fixed' | 'percentage';

export const DownpaymentDialog: React.FC<DownpaymentDialogProps> = ({
  open,
  onCancel,
  onSave,
  initialDownpayment,
  guests,
  rooms,
  totalPrice,
  currency,
}) => {
  const dispatch = useDispatch();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [calculationType, setCalculationType] = useState<CalculationType>('fixed');
  const [percentageValue, setPercentageValue] = useState<number>(25);

  // Calculate amount based on percentage
  const calculateAmountFromPercentage = (percentage: number): number => {
    return (totalPrice * percentage) / 100;
  };

  const toFormSchema = (downpayment: DownpaymentFormType): DownpaymentFormType => {
    return {
      downpaymentDueDate: downpayment.downpaymentDueDate,
      downpaymentAmount: downpayment.downpaymentAmount,
      downpaymentRemark: downpayment.downpaymentRemark
    };
  };
  const toObjSchema = (downpayment: DownpaymentFormType): DownpaymentFormType => {
    return {
      downpaymentDueDate: downpayment.downpaymentDueDate,
      downpaymentAmount: downpayment.downpaymentAmount,
      downpaymentRemark: downpayment.downpaymentRemark
    };
  };
  const form = useForm({
    mode: 'all',
    resolver: yupResolver(downpaymentSchema) as any,
    defaultValues: initialDownpayment
  });

  // Update amount when percentage changes
  useEffect(() => {
    if (calculationType === 'percentage') {
      const calculatedAmount = calculateAmountFromPercentage(percentageValue);
      form.setValue('downpaymentAmount', calculatedAmount);
    }
  }, [percentageValue, calculationType, totalPrice, form]);

  // Initial calculation when component loads with percentage mode
  useEffect(() => {
    if (calculationType === 'percentage' && initialDownpayment) {
      const calculatedAmount = calculateAmountFromPercentage(percentageValue);
      form.setValue('downpaymentAmount', calculatedAmount);
    }
  }, []);

  const validateDownpayment = (downpayment: DownpaymentFormType): string | null => {
    if (!isDownpaymentValid(toObjSchema(downpayment))) {
      return i18next.t('offer-info-downpayment-validation-error');
    }
    return null;
  };

  // Watch cancellation rules changes for real-time validation
  useEffect(() => {
    if (open) {
      const subscription = form.watch((value) => {
        const validationError = validateDownpayment(form.getValues());
        setValidationError(validationError);
      });
      return () => subscription.unsubscribe();
    }
  }, [open, form]);

  const handleSave = () => {
    const values = form.getValues();
    const validationError = validateDownpayment(values);

    if (validationError) {
      setValidationError(validationError);
      return;
    }

    setIsSaving(true);
    onSave(toObjSchema(values))
      .then(() => {
        setValidationError(null);
        dispatchMessage(dispatch, i18next.t('offer-info-downpayment-saved'));
      })
      .catch((error) => {
        dispatchException(dispatch, error);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleCancel = () => {
    form.reset(initialDownpayment);
    onCancel();
    setValidationError(null);
  };

  const handleCalculationTypeChange = (newType: CalculationType) => {
    setCalculationType(newType);

    // If switching to percentage, calculate the amount immediately
    if (newType === 'percentage') {
      const calculatedAmount = calculateAmountFromPercentage(percentageValue);
      form.setValue('downpaymentAmount', calculatedAmount);
    }
  };

  return (
    <SimpleDialog
      title={i18next.t('offer-info-dialog-downpayment-title')}
      open={open}
      setOpen={() => onCancel()}
      fullWidth
      maxWidth="md"
      customActions={<>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleCancel}
          startIcon={<CancelIcon />}
          disabled={isSaving}
        >
          {i18next.t('cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          startIcon={<SaveIcon />}
          disabled={isSaving}
        >
          {i18next.t('apply')}
        </Button>
      </>}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <SimpleTable fullWidth={false}
            rows={[
              [
                i18next.t('offer-info-dialog-guests'),
                guests
              ],
              [
                i18next.t('offer-info-dialog-rooms'),
                rooms
              ],
              [
                i18next.t('offer-info-dialog-total-amount'),
                <Price price={totalPrice} currency={currency} />
              ]
            ]}
          />
        </Grid>
        <Grid item xs={12}>
          {i18next.t('offer-info-downpayment-calculation-type')}
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <RadioGroup row value={calculationType} onChange={e => handleCalculationTypeChange(e.target.value as CalculationType)}>
              <FormControlLabel value="fixed" control={<Radio disabled={isSaving} />} label={i18next.t('offer-info-downpayment-fixed-amount')} />
              <FormControlLabel value="percentage" control={<Radio disabled={isSaving} />} label={i18next.t('offer-info-downpayment-percentage-amount')} />
            </RadioGroup>
          </FormControl>
        </Grid>
        {calculationType === 'percentage' && (<>
          <Grid item xs={12} md={3}>
            <InputPercentageTextField
              value={percentageValue}
              onChange={(event: any) => setPercentageValue(Number(event.target.value))}
              label={i18next.t('offer-info-downpayment-percentage')}
              required
              disabled={isSaving}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormInputCurrency
              name="downpaymentAmount"
              control={form.control}
              label={i18next.t('offer-info-downpayment-amount')}
              currency={currency}
              required
              disabled={isSaving}
            />
          </Grid>
        </>)}

        {calculationType === 'fixed' && (<>
          <Grid item xs={12} md={6}>
            <FormInputCurrency
              name="downpaymentAmount"
              control={form.control}
              label={i18next.t('offer-info-downpayment-amount')}
              currency={currency}
              required
              disabled={isSaving}
            />
          </Grid>
        </>)}


        {/* Due Date */}
        <Grid item xs={12} md={6}>
          <FormInputDate
            name="downpaymentDueDate"
            control={form.control}
            label={i18next.t('offer-info-downpayment-due-date')}
            required
            disabled={isSaving}
          />
        </Grid>

        {/* Remark */}
        <Grid item xs={12}>
          <FormInputTextArea
            name="downpaymentRemark"
            control={form.control}
            label={i18next.t('offer-info-downpayment-remark')}
            required={false}
            rows={3}
            disabled={isSaving}
          />
        </Grid>

        {validationError && (
          <Grid item xs={12}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          </Grid>
        )}
      </Grid>
    </SimpleDialog>
  );
};
